import { Box, Button, TextField, makeStyles } from '@material-ui/core';
import React, { FC, useState } from 'react';
import { ChromePicker } from 'react-color';
import { useDispatch } from 'react-redux';
import useComponentVisible from 'src/hooks/useComponentVisible';
import colorRectangle from 'src/images/colorRectangle.png';
import { RootState, useSelector } from 'src/store';
import {
  editText,
  editFontWeight,
  editColor,
  editFontSize,
  editTextAlign,
  editFontFamily
} from 'src/store/modules/editText';

import Plus from 'src/components/Icon/Plus';
import Minus from 'src/components/Icon/Minus';
import AlignLeft from 'src/components/Icon/AlignLeft';
import AlignCenter from 'src/components/Icon/AlignCenter';
import AlignRight from 'src/components/Icon/AlignRight';

import ChevronUp from 'src/components/Icon/ChevronUp';
import ChevronDown from 'src/components/Icon/ChevronDown';
import FontFamilyPicker from 'src/components/editor/FontFamilyPicker';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import AddLiquidModal from 'src/components/Modal/AddLiquidModal';

interface editTextProps {
  menuTitle: string;
  type: string;
}

const FONT_WEIGHT_ENUM: any = {
  300: 'Light',
  400: 'Regular',
  500: 'Medium',
  600: 'SemiBold',
  700: 'Bold',
  '': 'Custom'
};

const useStyles = makeStyles(() => ({
  root: {
    padding: '20px 16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  },
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
    border: '1px solid #E5E8EB',
    borderRadius: '8px'
  },
  fontBox: {
    width: '100%',
    height: '44px',
    padding: '0 12px',
    cursor: 'pointer',
    fontFamily: "'Poppins', sans-serif",
    fontSize: '13px',
    fontWeight: 400,
    color: '#191F28',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    border: '1px solid #E5E8EB',
    borderRadius: '8px',
    backgroundColor: '#F7F8FA',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: '#F2F3F5'
    }
  },
  input: {
    minHeight: '72px',
    maxHeight: '128px',
    padding: '12px',
    outline: 'none',
    overflow: 'auto',
    border: '1px solid #E5E8EB',
    borderRadius: '8px',
    backgroundColor: '#F7F8FA',
    '&:focus': {
      borderColor: '#191F28',
      outline: 'none'
    }
  },
  inputRoot: {
    padding: 0,
    height: '100%',
    display: 'block',
    overflow: 'auto'
  },
  inputText: {
    fontFamily: "'Poppins', sans-serif",
    fontSize: '13px',
    fontWeight: 400,
    lineHeight: '1.5',
    color: '#191F28'
  },

  bottomBtn: {
    padding: '8px 12px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    border: '1px solid #E5E8EB',
    borderRadius: '8px',
    backgroundColor: '#F7F8FA'
  },
  btnMinusPlus: {
    maxWidth: '32px',
    minWidth: '32px',
    maxHeight: '32px',
    minHeight: '32px',
    padding: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: '6px',
    backgroundColor: '#FFFFFF',
    border: '1px solid #E5E8EB',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: '#F2F3F5'
    }
  },

  fontNumber: {
    width: '48px',
    height: '32px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    fontFamily: "'Poppins', sans-serif",
    fontSize: '13px',
    fontWeight: 500,
    color: '#191F28',
    borderRadius: '6px',
    border: '1px solid #E5E8EB',
    backgroundColor: '#FFFFFF'
  },

  buttonWrapper: {
    maxWidth: '32px',
    minWidth: '32px',
    maxHeight: '32px',
    minHeight: '32px',
    borderRadius: '6px'
  },
  buttonImage: {
    maxWidth: '24px',
    minWidth: '24px',
    maxHeight: '24px',
    minHeight: '24px'
  },

  boxWrapper: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  colorBox: {
    padding: '6px 10px',
    borderRadius: '6px',
    backgroundColor: '#F2F3F5',
    textAlign: 'center',
    color: '#191F28',
    fontFamily: "'Poppins', sans-serif",
    fontSize: '12px',
    fontWeight: 400
  },
  textAlign: {
    cursor: 'pointer',
    padding: '6px',
    borderRadius: '6px',
    transition: 'background-color 0.2s ease',
    '&:hover': {
      backgroundColor: '#F2F3F5'
    }
  }
}));

const EditMultiText: FC<editTextProps> = ({ menuTitle, type }) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const currentIndex = useSelector(
    (state: RootState) => state.imageUpload.currentIndex
  );
  const text = useSelector((state: RootState) => state.editText.text);
  const fontSize = useSelector((state: RootState) => state.editText.fontSize);
  const fontWeight = useSelector(
    (state: RootState) => state.editText.fontWeight
  );
  const color = useSelector((state: RootState) => state.editText.color);
  const fontFamily = useSelector(
    (state: RootState) => state.editText.fontFamily
  );
  const textAlign = useSelector((state: RootState) => state.editText.textAlign);

  const {
    refPicker,
    refButton1,
    refButton2,
    isComponentVisible,
    setIsComponentVisible
  } = useComponentVisible(false);

  const [isFontFamily, setIsFontFamily] = useState(false);
  const [modalType, setModalType] = useState<string | null>(null);
  const handleColorPicker = () => {
    setIsComponentVisible(!isComponentVisible);
  };

  const handleText = e => {
    // const textSplit = e.target.value.split('');

    // if (
    //   textSplit[textSplit.length - 1] === '\\' ||
    //   textSplit[textSplit.length - 1] === '"' ||
    //   textSplit[textSplit.length - 1] === "'"
    // )
    //   return;
    const newTexts = text?.[type]?.slice();

    newTexts.splice(currentIndex, 1, e.target.value);
    // newTexts.splice(currentIndex, 1, e.target.value.split('\n').join('\\n'));
    // const newText = JSON.parse(
    //   `{"${type}": [${newTexts?.map((_: any, idx: number) => {
    //     const newT = newTexts?.[idx].split('\n').join('\\n');
    //     return `"${newT}"`;
    //   })}]}`
    // );

    const newText = {
      [type]: newTexts?.map((_: any, idx: number) => {
        const newT = newTexts?.[idx];
        return `${newT}`;
      })
    };

    dispatch(editText({ ...text, ...newText }));
  };

  const handleFontSize = f => {
    const newFontSize = JSON.parse(`{"${type}": ${fontSize[type] + f}}`);
    dispatch(editFontSize({ ...fontSize, ...newFontSize }));
  };

  const handleFontFamily = (family: string, weight: number | string) => {
    const newFontFamily = JSON.parse(`{"${type}": "${family}"}`);
    const newFontWeight = JSON.parse(`{"${type}": "${weight}"}`);
    dispatch(editFontFamily({ ...fontFamily, ...newFontFamily }));
    dispatch(editFontWeight({ ...fontWeight, ...newFontWeight }));
    setIsFontFamily(false);
  };

  const handleColor = c => {
    const newColor = JSON.parse(`{"${type}": "${c.hex}"}`);
    dispatch(editColor({ ...color, ...newColor }));
  };

  const handleTextAlign = (text: string) => {
    const newTextAlign = JSON.parse(`{"${type}": "${text}"}`);
    dispatch(editTextAlign({ ...textAlign, ...newTextAlign }));
  };

  return (
    <div className={classes.root}>
      <div
        style={{
          fontSize: '13px',
          fontWeight: 600,
          fontFamily: "'Poppins', sans-serif",
          color: '#191F28',
          letterSpacing: '0.02em'
        }}
      >
        {menuTitle}
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '0.5rem'
        }}
      >
        <div
          className={classes.fontBox}
          onClick={() => {
            setIsFontFamily(!isFontFamily);
          }}
        >
          <span>
            {`${fontFamily[type]} [${FONT_WEIGHT_ENUM[fontWeight[type]]}]`}
          </span>
          {isFontFamily ? (
            <ChevronUp
              style={{
                width: '1.5rem',
                height: '1.5rem'
              }}
            />
          ) : (
            <ChevronDown
              style={{
                width: '1.5rem',
                height: '1.5rem'
              }}
            />
          )}
        </div>
        {isFontFamily && (
          <FontFamilyPicker
            oriFontFamily={fontFamily[type]}
            oriFontWeight={fontWeight[type]}
            handleFontFamily={handleFontFamily}
          />
        )}
        <TextField
          className={classes.input}
          id="standard-multiline-flexible"
          multiline
          InputProps={{
            classes: {
              root: classes.inputRoot,
              input: classes.inputText
            },
            disableUnderline: true
          }}
          value={text?.[type]?.[currentIndex]}
          onChange={handleText}
          // onKeyPress={handleKey}
        />
        {!isFontFamily && (
          <Button
            style={{
              position: 'absolute',
              top: 120,
              right: 20,
              minWidth: 18,
              minHeight: 18,
              textAlign: 'center',
              padding: 0
            }}
            onClick={() =>
              setModalType(prevType => (prevType === type ? null : type))
            }
          >
            <AddCircleOutlineIcon fontSize="small" />
          </Button>
        )}
        {modalType && (
          <AddLiquidModal type={modalType} setModaltype={setModalType} />
        )}
        <div className={classes.bottomBtn}>
          <Box style={{ display: 'flex', gap: '1rem' }}>
            <Button
              className={classes.btnMinusPlus}
              disabled={fontSize[type] === 5 / 8}
              onClick={() => handleFontSize(-1 / 16)}
            >
              <Minus stroke={fontSize[type] === 5 / 8 ? '#BDBDBD' : '#000'} />
            </Button>
            <Button
              className={classes.btnMinusPlus}
              disabled={fontSize[type] === 4}
              onClick={() => handleFontSize(1 / 16)}
            >
              <Plus stroke={fontSize[type] === 4 ? '#BDBDBD' : '#000'} />
            </Button>
          </Box>

          <div className={classes.fontNumber}>{(fontSize[type] ?? 1) * 16}</div>
        </div>
      </div>
      <div>
        <Box className={classes.boxWrapper}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Button
              ref={refButton1}
              onClick={handleColorPicker}
              className={classes.buttonWrapper}
            >
              <img
                alt="color"
                src={colorRectangle}
                className={classes.buttonImage}
              />
            </Button>
            <Button
              ref={refButton2}
              style={{
                maxWidth: '20px',
                minWidth: '20px',
                maxHeight: '20px',
                minHeight: '20px',
                padding: 0
              }}
              onClick={handleColorPicker}
            >
              <div
                style={{
                  width: '20px',
                  height: '20px',
                  backgroundColor: color[type],
                  border: '1px solid #E5E8EB',
                  borderRadius: 999
                }}
              />
            </Button>
            <div className={classes.colorBox}>{color[type]?.toUpperCase()}</div>
          </div>
        </Box>
        {isComponentVisible && (
          <div ref={refPicker} style={{ width: '100%', marginTop: '1rem' }}>
            <ChromePicker
              color={color[type]}
              onChange={handleColor}
              disableAlpha
              width="100%"
            />
          </div>
        )}
      </div>
      <Box className={classes.boxWrapper}>
        <div
          style={{
            display: 'flex',
            // flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '1rem'
          }}
        >
          <div
            onClick={() => handleTextAlign('left')}
            className={classes.textAlign}
            style={{
              width: '1.5rem',
              height: '1.5rem'
            }}
          >
            <AlignLeft
              stroke={textAlign[type] === 'left' ? '#000000' : '#BDBDBD'}
            />
          </div>
          <div
            onClick={() => handleTextAlign('center')}
            className={classes.textAlign}
            style={{
              width: '1.5rem',
              height: '1.5rem'
            }}
          >
            <AlignCenter
              stroke={textAlign[type] === 'center' ? '#000000' : '#BDBDBD'}
            />
          </div>
          <div
            onClick={() => handleTextAlign('right')}
            className={classes.textAlign}
            style={{
              width: '1.5rem',
              height: '1.5rem'
            }}
          >
            <AlignRight
              stroke={textAlign[type] === 'right' ? '#000000' : '#BDBDBD'}
            />
          </div>
        </div>
      </Box>
    </div>
  );
};

export default EditMultiText;
